import { AmountInput, Switch } from '@components/index';
import { STORAGE_KEYS } from '@constants/storage';
import { Event, sendMixpanelEvent } from '@core/analytics/mixpanel';
import useCurrency from '@hooks/useCurrency';
import { FeeCategoryDataType, ValidationError } from '@types';
import { getMaxAmountErrorMessage, sanitizeNumberInput } from '@utils/generic';
import { getLocalStorageItem } from '@utils/storage';

interface FeeCategorySectionProps {
  isCustomTerm: boolean;
  isEditGroup: boolean;
  setIsFeeCategoryEnabled: (value: boolean) => void;
  setFeeCategory: (value: boolean) => void;
  groupId: string;
  feeCategories: FeeCategoryDataType[];
  setFeeCategories: (categories: FeeCategoryDataType[]) => void;
  onInputChange: (fieldName: string, value: any) => void;
  isFeeConfigured: boolean;
  isFeeCategoryEnabled: boolean;
  feeCategory: boolean;
  groupMemberMaxAmount?: number;
  error?: ValidationError;
  setError?: (value: ValidationError) => void;
}

const FeeCategorySection = ({
  isCustomTerm,
  isEditGroup,
  setIsFeeCategoryEnabled,
  setFeeCategory,
  groupId,
  feeCategories,
  setFeeCategories,
  onInputChange,
  isFeeConfigured,
  isFeeCategoryEnabled,
  feeCategory,
  groupMemberMaxAmount,
  error,
  setError
}: FeeCategorySectionProps) => {
  const organisationId = getLocalStorageItem(STORAGE_KEYS.ORGANIZATION_ID);
  const branchId = getLocalStorageItem(STORAGE_KEYS.BRANCH_ID);
  const { currencyConfig } = useCurrency();

  const toggleFeeCategory = (value: boolean) => {
    setIsFeeCategoryEnabled(value);
    setFeeCategory(value);
    sendMixpanelEvent(
      Event.FEE_CATEGORY_ENABLED_OR_DISABLED_IN_ADD_OR_EDIT_GROUP,
      {
        org_id: organisationId,
        branch_id: branchId,
        group_id: groupId,
        status: value ? 'enabled' : 'disabled',
        flow: isEditGroup ? 'edit group' : 'add group'
      }
    );
  };

  const onCategoryAmountChange = (index: number, value: string) => {
    setError?.({ ...(error || {}), amount: '' });

    const updatedCategories = [...feeCategories];
    updatedCategories[index].value = sanitizeNumberInput(value);
    setFeeCategories(updatedCategories);

    const totalAmount = updatedCategories.reduce(
      (sum, category) => sum + (Number(category.value) || 0),
      0
    );

    onInputChange('amount', totalAmount.toString());
  };

  const handleFeeCategoryAmountBlur = () => {
    if (
      isCustomTerm ||
      !feeCategory ||
      groupMemberMaxAmount === undefined ||
      !setError
    )
      return;

    const totalCategoryAmount = feeCategories.reduce(
      (sum, category) => sum + (Number(category.value) || 0),
      0
    );

    if (totalCategoryAmount > groupMemberMaxAmount) {
      setError({
        ...(error || {}),
        amount: getMaxAmountErrorMessage(groupMemberMaxAmount)
      });
    }
  };

  return (
    <>
      {!isEditGroup && isFeeConfigured && (
        <div className="flex flex-col">
          <div className="flex items-start gap-2">
            <Switch
              handleClick={toggleFeeCategory}
              value={isFeeCategoryEnabled}
            />
            <div className="flex flex-col">
              <div className="mr-2 pb-1 text-base font-semibold text-cofeeNeutral10">
                Enable Fee category
              </div>
              <div className="text-sm text-cofeeNeutral7">
                Split the total amount into different fee categories.
              </div>
            </div>
          </div>
        </div>
      )}
      {feeCategory && !isCustomTerm && (
        <div>
          <div className="flex w-full gap-6">
            <div className="w-1/2 text-base text-grey">
              {isEditGroup && feeCategories.length > 0
                ? 'Fee Category'
                : 'Category'}
            </div>
            <div className="w-1/2 text-base text-grey">Amount</div>
          </div>
          {feeCategories.map(({ label, value }, index) => (
            <div key={label} className="mt-3 flex w-full gap-6">
              <div className="w-1/2 rounded-lg bg-grey10 px-4 py-3 text-base text-content">
                {label}
              </div>
              <div className="w-1/2 text-base text-content">
                <AmountInput
                  value={value}
                  onAmountChange={(newValue) =>
                    onCategoryAmountChange(index, newValue)
                  }
                  handleInputBlur={handleFeeCategoryAmountBlur}
                  placeholder={`Enter Amount (If a fee doesn't apply, enter ${currencyConfig.symbol} 0)`}
                  allowZero
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default FeeCategorySection;
